/**
 * Adapted from PlayCanvas Engine's gsplat-hybrid-renderer-scratch.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import type {
  GSplatBufferLeaf,
  GSplatGraphicsAdapter,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'

/** Manager-owned、Hybrid/Shadow borrowed 的单 View GPU scratch。 */
export class GSplatHybridRendererScratch {
  compactedSplatIds: GSplatBufferLeaf | null = null

  private readonly adapter: GSplatGraphicsAdapter
  private allocatedCompacted = 0
  private destroyed = false

  constructor(adapter: GSplatGraphicsAdapter) {
    this.adapter = adapter
  }

  get compactedCapacity(): number {
    return this.allocatedCompacted
  }

  get gpuBytes(): number {
    return this.compactedSplatIds?.byteLength ?? 0
  }

  ensureCompactedSplatIds(capacity: number): GSplatBufferLeaf | null {
    this.assertActive()
    if (!Number.isSafeInteger(capacity) || capacity < 0) {
      throw new Error(`非法 compacted splat capacity: ${capacity}`)
    }
    if (capacity <= this.allocatedCompacted) return this.compactedSplatIds
    const byteLength = capacity * Uint32Array.BYTES_PER_ELEMENT
    const byteLimit = Math.min(
      this.adapter.limits.maxBufferSize,
      this.adapter.limits.maxStorageBufferBindingSize
    )
    if (!Number.isSafeInteger(byteLength) || byteLength > byteLimit) {
      throw new Error(
        `GSplatHybridRendererScratch 超出 storage buffer limit: ${byteLength} > ${byteLimit}`
      )
    }

    const staged = this.adapter.createBuffer({
      label: 'GSplatHybridRendererScratch.compactedSplatIds',
      byteLength,
      usages: ['storage', 'copy-src', 'copy-dst'],
    })
    const old = this.compactedSplatIds
    this.compactedSplatIds = staged
    this.allocatedCompacted = capacity
    old?.dispose()
    return staged
  }

  dispose(): void {
    if (this.destroyed) return
    this.destroyed = true
    this.compactedSplatIds?.dispose()
    this.compactedSplatIds = null
    this.allocatedCompacted = 0
  }

  private assertActive(): void {
    if (this.destroyed) throw new Error('GSplatHybridRendererScratch 已销毁')
  }
}
